import { cn } from '@/lib/cn'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

type Variant = 'success' | 'error' | 'info'

interface Toast {
  id: number
  title: string
  description?: string
  variant: Variant
}

interface ToastCtx {
  show: (t: Omit<Toast, 'id'>) => void
}

const Ctx = createContext<ToastCtx | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const show = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++idRef.current
    // Cap the visible stack at 3 — older toasts drop off the top.
    setToasts((prev) => [...prev, { ...t, id }].slice(-3))
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id))
    }, 4000)
  }, [])

  const dismiss = (id: number) => setToasts((prev) => prev.filter((x) => x.id !== id))

  const ctx = useMemo(() => ({ show }), [show])

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-0 z-[60] flex w-full max-w-sm flex-col items-end gap-2 px-4 sm:bottom-6 sm:right-6 sm:px-0">
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, x: 24, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={{ left: 0, right: 0.8 }}
              onDragEnd={(_, info) => {
                if (info.offset.x > 80) dismiss(t.id)
              }}
              className={cn(
                'pointer-events-auto flex w-full max-w-sm cursor-grab items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-lg active:cursor-grabbing',
              )}
              // biome-ignore lint/a11y/useSemanticElements: motion.div needs role="status" — <output> cannot be animated here
              role="status"
            >
              <ToastIcon variant={t.variant} />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="text-sm font-medium text-foreground">{t.title}</p>
                {t.description ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  )
}

function ToastIcon({ variant }: { variant: Variant }) {
  if (variant === 'success') return <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" />
  if (variant === 'error') return <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
  return <Info className="mt-0.5 h-4 w-4 text-muted-foreground" />
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
